/**
 * PNG 部件精灵加载：把 gen/parts/ 下去色 WebP 转成 dataURL 填进对应 Part.sprite。
 *
 * 为什么转 dataURL：编辑器预览走 innerHTML 注入 SVG 没问题，但导出 PNG 是把 SVG 字符串
 * 丢进 <img src=blob:> 再画 canvas——SVG-as-image 模式禁止加载外部资源，引用 http(s) 的
 * <image> 会静默不显示；dataURL 是唯一两栖可用的引用方式。
 *
 * 新增部件：在 URLS 里加一行静态 import（Vite ?url），loadAllSprites 会自动填充。
 */
import { FACES, HAIR_BACKS, HAIR_SIDES, HAIR_FRONTS, HAIRS, BROWS, EYES, GLASSES, NOSES, MOUTHS, CLOTHES, ACCESSORIES, AGE_LINES } from './parts';
import faceOvalUrl from './gen/parts/face/oval.webp?url';
import faceRoundUrl from './gen/parts/face/round.webp?url';
import faceSlimUrl from './gen/parts/face/slim.webp?url';
import faceLongUrl from './gen/parts/face/long.webp?url';
import facePointedUrl from './gen/parts/face/pointed.webp?url';
import hairBobUrl from './gen/parts/hair_back/bob.webp?url';
import hairBunUrl from './gen/parts/hair_back/bun.webp?url';
import hairLongUrl from './gen/parts/hair_back/long.webp?url';
import hairPonyUrl from './gen/parts/hair_back/ponytail.webp?url';
import hairTwintailUrl from './gen/parts/hair_back/twintail.webp?url';
import hairLongWavyUrl from './gen/parts/hair_back/long_wavy.webp?url';
import sideNoneUrl from './gen/parts/hair_side/none.webp?url';
import sideBraidUrl from './gen/parts/hair_side/braid.webp?url';
import sideLooseUrl from './gen/parts/hair_side/loose.webp?url';
import frontBangsUrl from './gen/parts/hair_front/bangs.webp?url';
import frontBangsWispyUrl from './gen/parts/hair_front/bangs_wispy.webp?url';
import frontMiddlePartUrl from './gen/parts/hair_front/middle_part.webp?url';
import frontChoppyUrl from './gen/parts/hair_front/choppy.webp?url';
import browHeroicUrl from './gen/parts/brows/heroic.webp?url';
import browGrievousUrl from './gen/parts/brows/grievous.webp?url';
import browRaisedUrl from './gen/parts/brows/raised.webp?url';
import mBrowUrl from './gen/parts_male/brows/粗平眉.webp?url';
import eyeFrameUrl from './gen/parts/eyes/frame/upturned.webp?url';
import eyeIrisUrl from './gen/parts/eyes/iris/upturned.webp?url';
import eyeFrameRoundUrl from './gen/parts/eyes/frame/round.webp?url';
import eyeIrisRoundUrl from './gen/parts/eyes/iris/round.webp?url';
import eyeFrameGentleUrl from './gen/parts/eyes/frame/gentle.webp?url';
import eyeIrisGentleUrl from './gen/parts/eyes/iris/gentle.webp?url';
import eyeFrameDroopyUrl from './gen/parts/eyes/frame/droopy.webp?url';
import eyeIrisDroopyUrl from './gen/parts/eyes/iris/droopy.webp?url';
import noseSmallUrl from './gen/parts/nose/small.webp?url';
import noseDefinedUrl from './gen/parts/nose/defined.webp?url';
import mouthSmirkUrl from './gen/parts/mouth/smirk.webp?url';
import mouthSlightlyOpenUrl from './gen/parts/mouth/slightly_open.webp?url';
import mouthPressedUrl from './gen/parts/mouth/pressed.webp?url';
import mouthSmileUrl from './gen/parts/mouth/smile.webp?url';
import clothTshirtUrl from './gen/parts/clothes/tshirt.webp?url';
import clothHoodieUrl from './gen/parts/clothes/hoodie.webp?url';
import clothSuitUrl from './gen/parts/clothes/suit.webp?url';
import clothJacketUrl from './gen/parts/clothes/jacket.webp?url';
import clothSequinUrl from './gen/parts/clothes/sequin_dress.webp?url';
import clothJacketSweaterUrl from './gen/parts/clothes/jacket_sweater.webp?url';
import clothBowShirtUrl from './gen/parts/clothes/bow_shirt.webp?url';
import clothGownUrl from './gen/parts/clothes/gown.webp?url';
import clothHoodie2Url from './gen/parts/clothes/卫衣.webp?url';
import clothShirtTieFUrl from './gen/parts/clothes/衬衫领带.webp?url';
import accGlassesSquareUrl from './gen/parts/accessory/glasses_square.webp?url';
import accHoopEarringUrl from './gen/parts/accessory/hoop_earring.webp?url';
import ageWrinklesUrl from './gen/parts/age_lines/皱纹.webp?url';
// 男生首批（gen/parts_male/，首批源件名沿用中文名）
import mFaceNormalUrl from './gen/parts_male/face/普通脸.webp?url';
import mHairNormalUrl from './gen/parts_male/hair/普通头.webp?url';
import mHairSidepartUrl from './gen/parts_male/hair/三七分.webp?url';
import mHairSlickbackUrl from './gen/parts_male/hair/背头.webp?url';
import mEyeFrameUrl from './gen/parts_male/eyes/frame/眼睛1.webp?url';
import mEyeIrisUrl from './gen/parts_male/eyes/iris/眼睛1.webp?url';
import mEye2FrameUrl from './gen/parts_male/eyes/frame/眼睛2.webp?url';
import mEye2IrisUrl from './gen/parts_male/eyes/iris/眼睛2.webp?url';
import mEye3FrameUrl from './gen/parts_male/eyes/frame/眼睛3.webp?url';
import mEye3IrisUrl from './gen/parts_male/eyes/iris/眼睛3.webp?url';
import mNoseUrl from './gen/parts_male/nose/鼻子1.webp?url';
import mNose2Url from './gen/parts_male/nose/鼻子2.webp?url';
import mMouthUrl from './gen/parts_male/mouth/嘴巴1.webp?url';
import mLaughUrl from './gen/parts_male/mouth/大笑.webp?url';
import mSmirkUrl from './gen/parts_male/mouth/嘴角微翘.webp?url';
import mSlightlyOpenUrl from './gen/parts_male/mouth/嘴微张.webp?url';
import mSweaterUrl from './gen/parts_male/clothes/黑毛衣.webp?url';
import mShirtTieUrl from './gen/parts_male/clothes/衬衫领带.webp?url';
import mSuitUrl from './gen/parts_male/clothes/西装.webp?url';
import mHoodieUrl from './gen/parts_male/clothes/卫衣.webp?url';
import mSportswearUrl from './gen/parts_male/clothes/运动服.webp?url';
import mGlassesSquareUrl from './gen/parts_male/glasses/方框眼镜.webp?url';

/** 部件对象 → Vite 资源 URL。加新件：import 一行 + 这里一行 */
const SPRITE_URLS: Array<[part: { sprite?: string }, url: string]> = [
  [FACES.find(p => p.id === 'oval')!, faceOvalUrl],
  [FACES.find(p => p.id === 'round')!, faceRoundUrl],
  [FACES.find(p => p.id === 'slim')!, faceSlimUrl],
  [FACES.find(p => p.id === 'pointed')!, facePointedUrl],
  [FACES.find(p => p.id === 'long')!, faceLongUrl],
  [HAIR_BACKS.find(p => p.id === 'sBob')!, hairBobUrl],
  [HAIR_BACKS.find(p => p.id === 'sBun')!, hairBunUrl],
  [HAIR_BACKS.find(p => p.id === 'sLong')!, hairLongUrl],
  [HAIR_BACKS.find(p => p.id === 'sPonytail')!, hairPonyUrl],
  [HAIR_BACKS.find(p => p.id === 'sTwintail')!, hairTwintailUrl],
  [HAIR_BACKS.find(p => p.id === 'sLongWavy')!, hairLongWavyUrl],
  [HAIR_SIDES.find(p => p.id === 'sSideNone')!, sideNoneUrl],
  [HAIR_SIDES.find(p => p.id === 'sSideBraid')!, sideBraidUrl],
  [HAIR_SIDES.find(p => p.id === 'sSideLoose')!, sideLooseUrl],
  [HAIR_FRONTS.find(p => p.id === 'sBangs')!, frontBangsUrl],
  [HAIR_FRONTS.find(p => p.id === 'sBangsWispy')!, frontBangsWispyUrl],
  [HAIR_FRONTS.find(p => p.id === 'sMiddlePart')!, frontMiddlePartUrl],
  [HAIR_FRONTS.find(p => p.id === 'sChoppy')!, frontChoppyUrl],
  [BROWS.find(p => p.id === 'sHeroic')!, browHeroicUrl],
  [BROWS.find(p => p.id === 'sGrievous')!, browGrievousUrl],
  [BROWS.find(p => p.id === 'sRaised')!, browRaisedUrl],
  [BROWS.find(p => p.id === 'mBrow1')!, mBrowUrl],
  [EYES.find(p => p.id === 'sUpturned')!, eyeFrameUrl],
  [EYES.find(p => p.id === 'sRound')!, eyeFrameRoundUrl],
  [EYES.find(p => p.id === 'sGentle')!, eyeFrameGentleUrl],
  [EYES.find(p => p.id === 'sDroopy')!, eyeFrameDroopyUrl],
  [NOSES.find(p => p.id === 'sSmall')!, noseSmallUrl],
  [NOSES.find(p => p.id === 'sDefined')!, noseDefinedUrl],
  [MOUTHS.find(p => p.id === 'sSmirk')!, mouthSmirkUrl],
  [MOUTHS.find(p => p.id === 'sSlightlyOpen')!, mouthSlightlyOpenUrl],
  [MOUTHS.find(p => p.id === 'sPressed')!, mouthPressedUrl],
  [MOUTHS.find(p => p.id === 'sSmile')!, mouthSmileUrl],
  [CLOTHES.find(p => p.id === 'sTshirt')!, clothTshirtUrl],
  [CLOTHES.find(p => p.id === 'sHoodie')!, clothHoodieUrl],
  [CLOTHES.find(p => p.id === 'sSuit')!, clothSuitUrl],
  [CLOTHES.find(p => p.id === 'sJacket')!, clothJacketUrl],
  [CLOTHES.find(p => p.id === 'sSequin')!, clothSequinUrl],
  [CLOTHES.find(p => p.id === 'sJacketSweater')!, clothJacketSweaterUrl],
  [CLOTHES.find(p => p.id === 'sBowShirt')!, clothBowShirtUrl],
  [GLASSES.find(p => p.id === 'sGlassesSquare')!, accGlassesSquareUrl],
  [AGE_LINES.find(p => p.id === 'sWrinkles')!, ageWrinklesUrl],
  [ACCESSORIES.find(p => p.id === 'sHoopEarring')!, accHoopEarringUrl],
  [CLOTHES.find(p => p.id === 'sGown')!, clothGownUrl],
  [CLOTHES.find(p => p.id === 'sHoodie2')!, clothHoodie2Url],
  [CLOTHES.find(p => p.id === 'sShirtTie')!, clothShirtTieFUrl],
  // 男生首批
  [FACES.find(p => p.id === 'mNormal')!, mFaceNormalUrl],
  [HAIRS.find(p => p.id === 'mHairNormal')!, mHairNormalUrl],
  [HAIRS.find(p => p.id === 'mHairSidepart')!, mHairSidepartUrl],
  [HAIRS.find(p => p.id === 'mSlickback')!, mHairSlickbackUrl],
  [EYES.find(p => p.id === 'mEye1')!, mEyeFrameUrl],
  [EYES.find(p => p.id === 'mEye2')!, mEye2FrameUrl],
  [EYES.find(p => p.id === 'mEye3')!, mEye3FrameUrl],
  [NOSES.find(p => p.id === 'mNose1')!, mNoseUrl],
  [NOSES.find(p => p.id === 'mNose2')!, mNose2Url],
  [MOUTHS.find(p => p.id === 'mMouth1')!, mMouthUrl],
  [MOUTHS.find(p => p.id === 'mLaugh')!, mLaughUrl],
  [MOUTHS.find(p => p.id === 'mSmirk')!, mSmirkUrl],
  [MOUTHS.find(p => p.id === 'mSlightlyOpen')!, mSlightlyOpenUrl],
  [CLOTHES.find(p => p.id === 'mSweater')!, mSweaterUrl],
  [CLOTHES.find(p => p.id === 'mShirtTie')!, mShirtTieUrl],
  [CLOTHES.find(p => p.id === 'mSuit')!, mSuitUrl],
  [CLOTHES.find(p => p.id === 'mHoodie')!, mHoodieUrl],
  [CLOTHES.find(p => p.id === 'mSportswear')!, mSportswearUrl],
  [GLASSES.find(p => p.id === 'mGlassesSquare')!, mGlassesSquareUrl],
];

/** 眼睛虹膜单独登记：frame 在 SPRITE_URLS，iris 染瞳色由渲染器单独叠 */
const EYE_IRIS_URLS: Array<[part: { spriteIris?: string }, url: string]> = [
  [EYES.find(p => p.id === 'sUpturned')!, eyeIrisUrl],
  [EYES.find(p => p.id === 'sRound')!, eyeIrisRoundUrl],
  [EYES.find(p => p.id === 'sGentle')!, eyeIrisGentleUrl],
  [EYES.find(p => p.id === 'sDroopy')!, eyeIrisDroopyUrl],
  [EYES.find(p => p.id === 'mEye1')!, mEyeIrisUrl],
  [EYES.find(p => p.id === 'mEye2')!, mEye2IrisUrl],
  [EYES.find(p => p.id === 'mEye3')!, mEye3IrisUrl],
];

function toDataURL(url: string): Promise<string> {
  // low 优先级：部件是「进页前就该备好」的后台资源，⛔ 抢首屏主包/场景图的带宽
  return fetch(url, { priority: 'low' })
    .then((r) => {
      if (!r.ok) throw new Error(`sprite fetch ${r.status}: ${url}`);
      return r.blob();
    })
    .then(
      (blob) =>
        new Promise<string>((resolve, reject) => {
          const fr = new FileReader();
          fr.onload = () => resolve(fr.result as string);
          fr.onerror = () => reject(fr.error);
          fr.readAsDataURL(blob);
        }),
    );
}

/** 部件对象 → Vite 原始资源 URL（67 批：捏脸页缩略图直接引短 URL，避免内嵌 dataURL 巨串）。
 *  模块加载即建表，不等 dataURL 转换完成 */
const RAW_URL = new WeakMap<object, string>();
SPRITE_URLS.forEach(([part, url]) => RAW_URL.set(part, url));
EYE_IRIS_URLS.forEach(([part, url]) => RAW_URL.set(part, url));
export function spriteRawUrl(part: object): string | undefined {
  return RAW_URL.get(part);
}

/** 把所有精灵转 dataURL 并写入 Part.sprite / spriteIris；入口文件须 await 之后再挂编辑器 */
export async function loadAllSprites(): Promise<void> {
  await Promise.all(SPRITE_URLS.map(async ([part, url]) => { part.sprite = await toDataURL(url); }));
  await Promise.all(EYE_IRIS_URLS.map(async ([part, url]) => { part.spriteIris = await toDataURL(url); }));
}
